import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FAQ from '../components/FAQ';
import { usePageTitle } from '../hooks/usePageTitle';
import './EnginePage.css';

const EnginePage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  usePageTitle('The Engine - Synthetic Juror, Fact Finder, Trial Signal');

  // Tab state for each product
  const [jurorTab, setJurorTab] = useState('overview');
  const [finderTab, setFinderTab] = useState('overview');
  const [signalTab, setSignalTab] = useState('overview');

  // Tab configs with translation keys
  const syntheticJurorTabs = [
    { id: 'overview', labelKey: 'engine.tabs.overview' },
    { id: 'evidence', labelKey: 'engine.tabs.evidence' },
    { id: 'cognitive', labelKey: 'engine.tabs.cognitive' },
    { id: 'deliberation', labelKey: 'engine.tabs.deliberation' },
  ];

  const factFinderTabs = [
    { id: 'overview', labelKey: 'engine.tabs.overview' },
    { id: 'corpus', labelKey: 'engine.tabs.corpus' },
    { id: 'testing', labelKey: 'engine.tabs.testing' },
    { id: 'intelligence', labelKey: 'engine.tabs.intelligence' },
  ];

  const trialSignalTabs = [
    { id: 'overview', labelKey: 'engine.tabs.overview' },
    { id: 'factors', labelKey: 'engine.tabs.factors' },
    { id: 'sensitivity', labelKey: 'engine.tabs.sensitivity' },
    { id: 'witness', labelKey: 'engine.tabs.witness' },
  ];

  // Handle anchor scrolling
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash]);

  // Helper to get array items from translations
  const getItems = (key: string): string[] => {
    return t(key, { returnObjects: true }) as string[];
  };

  return (
    <div className="engine-page">
      {/* Hero Section */}
      <section className="engine-hero">
        <div className="container">
          <span className="section-label">{t('engine.label')}</span>
          <h1>
            <span className="hero-highlight">{t('home.hero.titleHighlight')}</span> {t('nav.engine')}
          </h1>
          <p className="engine-subtitle">
            {t('engine.subtitle')}
          </p>
        </div>
      </section>

      {/* ===== PRODUCT 1: Synthetic Juror ===== */}
      <section id="synthetic-juror" className="product-section product-juror">
        <div className="container">
          {/* Product Header */}
          <div className="product-header">
            <div className="product-logo">
              <span className="logo-text">Synthetic</span>
              <span className="logo-highlight">Juror<sup>™</sup></span>
            </div>
            <p className="product-tagline">{t('engine.products.syntheticJuror.tagline')}</p>
          </div>

          {/* Tab Navigation */}
          <div className="product-tabs">
            {syntheticJurorTabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${jurorTab === tab.id ? 'active' : ''}`}
                onClick={() => setJurorTab(tab.id)}
              >
                {t(tab.labelKey)}
                {jurorTab === tab.id && <span className="tab-indicator" />}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="product-layout">
            <div className="product-content">
              {jurorTab === 'overview' && (
                <div className="tab-panel">
                  <h3>{t('engine.products.syntheticJuror.overview.title')}</h3>
                  <p className="lead-text">
                    {t('engine.products.syntheticJuror.overview.lead')}
                  </p>
                  <p>
                    {t('engine.products.syntheticJuror.overview.description')}
                  </p>
                  <p className="emphasis">{t('engine.products.syntheticJuror.overview.emphasis')}</p>
                  <div className="why-it-works">
                    <h4 className="why-it-works-title">{t('engine.products.syntheticJuror.overview.whyItWorks.title')}</h4>
                    <div className="why-it-works-grid">
                      <div className="why-it-works-item">
                        <h5>{t('engine.products.syntheticJuror.overview.whyItWorks.venue.heading')}</h5>
                        <p>{t('engine.products.syntheticJuror.overview.whyItWorks.venue.text')}</p>
                      </div>
                      <div className="why-it-works-item">
                        <h5>{t('engine.products.syntheticJuror.overview.whyItWorks.scale.heading')}</h5>
                        <p>{t('engine.products.syntheticJuror.overview.whyItWorks.scale.text')}</p>
                      </div>
                      <div className="why-it-works-item">
                        <h5>{t('engine.products.syntheticJuror.overview.whyItWorks.validation.heading')}</h5>
                        <p>{t('engine.products.syntheticJuror.overview.whyItWorks.validation.text')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {jurorTab === 'evidence' && (
                <div className="tab-panel">
                  <h3>{t('engine.products.syntheticJuror.evidence.title')}</h3>
                  <p className="lead-text">
                    {t('engine.products.syntheticJuror.evidence.lead')}
                  </p>
                  <ul className="feature-list-clean">
                    {getItems('engine.products.syntheticJuror.evidence.items').map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p>
                    {t('engine.products.syntheticJuror.evidence.description')}
                  </p>
                </div>
              )}

              {jurorTab === 'cognitive' && (
                <div className="tab-panel">
                  <h3>{t('engine.products.syntheticJuror.cognitive.title')}</h3>
                  <p className="lead-text emphasis">
                    {t('engine.products.syntheticJuror.cognitive.lead')}
                  </p>
                  <p>{t('engine.products.syntheticJuror.cognitive.intro')}</p>
                  <ul className="feature-list-clean">
                    {getItems('engine.products.syntheticJuror.cognitive.items').map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p className="result-text">
                    {t('engine.products.syntheticJuror.cognitive.result')}
                  </p>
                </div>
              )}

              {jurorTab === 'deliberation' && (
                <div className="tab-panel">
                  <h3>{t('engine.products.syntheticJuror.deliberation.title')}</h3>
                  <p className="lead-text">
                    {t('engine.products.syntheticJuror.deliberation.lead')}
                  </p>
                  <ul className="feature-list-clean">
                    {getItems('engine.products.syntheticJuror.deliberation.items').map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p>
                    {t('engine.products.syntheticJuror.deliberation.description')}
                  </p>
                </div>
              )}

              <Link to="/contact" className="btn btn-primary">{t('engine.requestAccess')}</Link>
            </div>

            {/* Illustration */}
            <div className="product-illustration juror-demo">
              <div className="demo-container">
                {/* Juror Card Stack */}
                <div className="juror-stack-demo">
                  <div className="profile-card-demo stack-3"></div>
                  <div className="profile-card-demo stack-2"></div>
                  <div className="profile-card-demo stack-1">
                    <div className="profile-header-demo">
                      <div className="profile-avatar-demo">
                        <span>MR</span>
                      </div>
                      <div className="profile-info-demo">
                        <span className="name">{t('home.showcase.jurorCard.name')}</span>
                        <span className="venue">{t('home.showcase.jurorCard.venue')}</span>
                      </div>
                      <div className="stance-badge plaintiff">P</div>
                    </div>
                    <div className="profile-details-demo">
                      <div className="detail-row-demo">
                        <span className="label">{t('home.showcase.jurorCard.occupation')}</span>
                        <span className="value">{t('home.showcase.jurorCard.occupationValue')}</span>
                      </div>
                      <div className="detail-row-demo">
                        <span className="label">{t('home.showcase.jurorCard.education')}</span>
                        <span className="value">{t('home.showcase.jurorCard.educationValue')}</span>
                      </div>
                      <div className="detail-row-demo">
                        <span className="label">{t('home.showcase.jurorCard.anchor')}</span>
                        <span className="value trait">{t('home.showcase.jurorCard.anchorValue')}</span>
                      </div>
                    </div>
                    <div className="traits-demo">
                      <span className="trait-tag-demo">{t('home.showcase.jurorCard.trait1')}</span>
                      <span className="trait-tag-demo">{t('home.showcase.jurorCard.trait2')}</span>
                    </div>
                  </div>
                </div>

                {/* Panel Composition Widget */}
                <div className="panel-widget-demo">
                  <div className="widget-header-demo">{t('home.showcase.panelWidget.title')}</div>
                  <div className="composition-grid-demo">
                    <div className="dot plaintiff"></div>
                    <div className="dot plaintiff"></div>
                    <div className="dot plaintiff"></div>
                    <div className="dot plaintiff"></div>
                    <div className="dot undecided"></div>
                    <div className="dot undecided"></div>
                    <div className="dot plaintiff"></div>
                    <div className="dot undecided"></div>
                    <div className="dot defense"></div>
                    <div className="dot defense"></div>
                    <div className="dot defense"></div>
                    <div className="dot defense"></div>
                  </div>
                  <div className="legend-demo">
                    <span><i className="dot-p"></i> 5 {t('home.showcase.panelWidget.plaintiff')}</span>
                    <span><i className="dot-u"></i> 3 {t('home.showcase.panelWidget.undecided')}</span>
                    <span><i className="dot-d"></i> 4 {t('home.showcase.panelWidget.defense')}</span>
                  </div>
                </div>

                {/* Verdict Ring */}
                <div className="verdict-widget-demo">
                  <div className="verdict-ring-demo">
                    <svg viewBox="0 0 36 36">
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#e2e8f0" strokeWidth="3"/>
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#3b82f6" strokeWidth="3" strokeDasharray="62, 100" strokeLinecap="round"/>
                    </svg>
                    <span className="pct">62%</span>
                  </div>
                  <span className="verdict-label-demo">{t('home.showcase.verdictMini.likely')}<br/>{t('home.showcase.verdictMini.label')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PRODUCT 2: FactFinder ===== */}
      <section id="fact-finder" className="product-section product-factfinder">
        <div className="container">
          {/* Product Header */}
          <div className="product-header">
            <div className="product-logo">
              <span className="logo-text">Fact</span>
              <span className="logo-highlight">Finder<sup>™</sup></span>
            </div>
            <p className="product-tagline">{t('engine.products.factFinder.tagline')}</p>
          </div>

          {/* Tab Navigation */}
          <div className="product-tabs">
            {factFinderTabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${finderTab === tab.id ? 'active' : ''}`}
                onClick={() => setFinderTab(tab.id)}
              >
                {t(tab.labelKey)}
                {finderTab === tab.id && <span className="tab-indicator" />}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="product-layout">
            <div className="product-content">
              {finderTab === 'overview' && (
                <div className="tab-panel">
                  <h3>{t('engine.products.factFinder.overview.title')}</h3>
                  <p className="lead-text">
                    {t('engine.products.factFinder.overview.lead')}
                  </p>
                  <p>
                    {t('engine.products.factFinder.overview.description')}
                  </p>
                  <div className="why-it-works amber">
                    <h4 className="why-it-works-title">{t('engine.products.factFinder.overview.whyItWorks.title')}</h4>
                    <div className="why-it-works-grid">
                      <div className="why-it-works-item">
                        <h5>{t('engine.products.factFinder.overview.whyItWorks.scope.heading')}</h5>
                        <p>{t('engine.products.factFinder.overview.whyItWorks.scope.text')}</p>
                      </div>
                      <div className="why-it-works-item">
                        <h5>{t('engine.products.factFinder.overview.whyItWorks.corpus.heading')}</h5>
                        <p>{t('engine.products.factFinder.overview.whyItWorks.corpus.text')}</p>
                      </div>
                      <div className="why-it-works-item">
                        <h5>{t('engine.products.factFinder.overview.whyItWorks.interface.heading')}</h5>
                        <p>{t('engine.products.factFinder.overview.whyItWorks.interface.text')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {finderTab === 'corpus' && (
                <div className="tab-panel">
                  <h3>{t('engine.products.factFinder.corpus.title')}</h3>
                  <p className="lead-text">
                    {t('engine.products.factFinder.corpus.lead')}
                  </p>
                  <ul className="feature-list-clean amber">
                    {getItems('engine.products.factFinder.corpus.items').map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p>
                    {t('engine.products.factFinder.corpus.description')}
                  </p>
                </div>
              )}

              {finderTab === 'testing' && (
                <div className="tab-panel">
                  <h3>{t('engine.products.factFinder.testing.title')}</h3>
                  <p className="lead-text">
                    {t('engine.products.factFinder.testing.lead')}
                  </p>
                  <ul className="feature-list-clean amber">
                    {getItems('engine.products.factFinder.testing.items').map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p className="emphasis">{t('engine.products.factFinder.testing.emphasis')}</p>
                </div>
              )}

              {finderTab === 'intelligence' && (
                <div className="tab-panel">
                  <h3>{t('engine.products.factFinder.intelligence.title')}</h3>
                  <p className="lead-text">
                    {t('engine.products.factFinder.intelligence.lead')}
                  </p>
                  <ul className="feature-list-clean amber">
                    {getItems('engine.products.factFinder.intelligence.items').map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p>
                    {t('engine.products.factFinder.intelligence.description')}
                  </p>
                  <p className="disclaimer">
                    {t('engine.products.factFinder.intelligence.disclaimer')}
                  </p>
                </div>
              )}

              <Link to="/contact" className="btn btn-primary btn-amber">{t('engine.requestAccess')}</Link>
            </div>

            {/* Illustration */}
            <div className="product-illustration factfinder-demo">
              <div className="demo-container">
                {/* Judge Card */}
                <div className="judge-card-demo">
                  <div className="judge-header-demo">
                    <div className="judge-icon-demo">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
                      </svg>
                    </div>
                    <div className="judge-info-demo">
                      <span className="judge-name-demo">{t('home.showcase.judgeCard.name')}</span>
                      <span className="judge-court-demo">{t('home.showcase.judgeCard.court')}</span>
                      <span className="judge-appt-demo">{t('home.showcase.judgeCard.appointed')}</span>
                    </div>
                  </div>
                  <div className="judge-stats-demo">
                    <div className="stat-demo">
                      <span className="stat-value-demo">68%</span>
                      <span className="stat-label-demo">{t('home.showcase.judgeCard.mtd')}</span>
                    </div>
                    <div className="stat-demo">
                      <span className="stat-value-demo">41%</span>
                      <span className="stat-label-demo">{t('home.showcase.judgeCard.msj')}</span>
                    </div>
                    <div className="stat-demo">
                      <span className="stat-value-demo">2.3y</span>
                      <span className="stat-label-demo">{t('home.showcase.judgeCard.avg')}</span>
                    </div>
                  </div>
                </div>

                {/* MTD Ring */}
                <div className="mtd-widget-demo">
                  <div className="mtd-ring-demo">
                    <svg viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#fef3c7" strokeWidth="10"/>
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#d97706" strokeWidth="10" strokeDasharray="170 82" strokeLinecap="round" transform="rotate(-90 50 50)"/>
                    </svg>
                    <div className="mtd-center-demo">
                      <span className="mtd-value-demo">68%</span>
                      <span className="mtd-label-demo">{t('home.showcase.judgeCard.mtd')}</span>
                    </div>
                  </div>
                  <span className="mtd-title-demo">{t('home.showcase.motionSuccess')}</span>
                </div>

                {/* Signal Card */}
                <div className="signal-card-demo">
                  <div className="signal-icon-demo">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
                    </svg>
                  </div>
                  <div className="signal-content-demo">
                    <span className="signal-label-demo">{t('home.showcase.reasoningSignal.label')}</span>
                    <span className="signal-value-demo">{t('home.showcase.reasoningSignal.value')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PRODUCT 3: Trial Signal ===== */}
      <section id="trial-signal" className="product-section product-trialsignal">
        <div className="container">
          {/* Product Header */}
          <div className="product-header">
            <div className="product-logo">
              <span className="logo-text">Trial</span>
              <span className="logo-highlight">Signal<sup>™</sup></span>
            </div>
            <p className="product-tagline">{t('engine.products.trialSignal.tagline')}</p>
          </div>

          {/* Tab Navigation */}
          <div className="product-tabs">
            {trialSignalTabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${signalTab === tab.id ? 'active' : ''}`}
                onClick={() => setSignalTab(tab.id)}
              >
                {t(tab.labelKey)}
                {signalTab === tab.id && <span className="tab-indicator" />}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="product-layout">
            <div className="product-content">
              {signalTab === 'overview' && (
                <div className="tab-panel">
                  <h3>{t('engine.products.trialSignal.overview.title')}</h3>
                  <p className="lead-text">
                    {t('engine.products.trialSignal.overview.lead')}
                  </p>
                  <p>
                    {t('engine.products.trialSignal.overview.description')}
                  </p>
                  <div className="feature-highlights green">
                    <div className="highlight-item">
                      <span className="highlight-number">40+</span>
                      <span className="highlight-label">{t('engine.products.trialSignal.overview.stats.factors')}</span>
                    </div>
                    <div className="highlight-item">
                      <span className="highlight-number">Real-time</span>
                      <span className="highlight-label">{t('engine.products.trialSignal.overview.stats.sensitivity')}</span>
                    </div>
                    <div className="highlight-item">
                      <span className="highlight-number">Witness</span>
                      <span className="highlight-label">{t('engine.products.trialSignal.overview.stats.credibility')}</span>
                    </div>
                  </div>
                </div>
              )}

              {signalTab === 'factors' && (
                <div className="tab-panel">
                  <h3>{t('engine.products.trialSignal.factors.title')}</h3>
                  <p className="lead-text">
                    {t('engine.products.trialSignal.factors.lead')}
                  </p>
                  <ul className="feature-list-clean green">
                    {getItems('engine.products.trialSignal.factors.items').map((item, i) => (
                      <li key={i}><strong>{item.split(' — ')[0]}</strong>{item.includes(' — ') ? ` — ${item.split(' — ')[1]}` : ''}</li>
                    ))}
                  </ul>
                  <p>{t('engine.products.trialSignal.factors.description')}</p>
                </div>
              )}

              {signalTab === 'sensitivity' && (
                <div className="tab-panel">
                  <h3>{t('engine.products.trialSignal.sensitivity.title')}</h3>
                  <p className="lead-text">
                    {t('engine.products.trialSignal.sensitivity.lead')}
                  </p>
                  <ul className="feature-list-clean green">
                    {getItems('engine.products.trialSignal.sensitivity.items').map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p className="emphasis">{t('engine.products.trialSignal.sensitivity.emphasis')}</p>
                </div>
              )}

              {signalTab === 'witness' && (
                <div className="tab-panel">
                  <h3>{t('engine.products.trialSignal.witness.title')}</h3>
                  <p className="lead-text">
                    {t('engine.products.trialSignal.witness.lead')}
                  </p>
                  <ul className="feature-list-clean green">
                    {getItems('engine.products.trialSignal.witness.items').map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p>
                    {t('engine.products.trialSignal.witness.description')}
                  </p>
                </div>
              )}

              <Link to="/contact" className="btn btn-primary btn-green">{t('engine.requestAccess')}</Link>
            </div>

            {/* Illustration */}
            <div className="product-illustration trialsignal-demo">
              <div className="demo-container">
                {/* Analytics Card */}
                <div className="analytics-card-demo">
                  <div className="analytics-header-demo">
                    <span className="analytics-title-demo">{t('home.showcase.analytics.title')}</span>
                    <span className="analytics-badge-demo">{t('home.showcase.analytics.badge')}</span>
                  </div>
                  <div className="factor-bars-demo">
                    <div className="factor-row-demo">
                      <span className="factor-name-demo">{t('home.showcase.analytics.factor1')}</span>
                      <div className="factor-bar-demo">
                        <div className="factor-fill-demo high" style={{ width: '89%' }}></div>
                      </div>
                      <span className="factor-score-demo">89</span>
                    </div>
                    <div className="factor-row-demo">
                      <span className="factor-name-demo">{t('home.showcase.analytics.factor2')}</span>
                      <div className="factor-bar-demo">
                        <div className="factor-fill-demo medium" style={{ width: '72%' }}></div>
                      </div>
                      <span className="factor-score-demo">72</span>
                    </div>
                    <div className="factor-row-demo">
                      <span className="factor-name-demo">{t('home.showcase.analytics.factor3')}</span>
                      <div className="factor-bar-demo">
                        <div className="factor-fill-demo medium" style={{ width: '65%' }}></div>
                      </div>
                      <span className="factor-score-demo">65</span>
                    </div>
                    <div className="factor-row-demo">
                      <span className="factor-name-demo">{t('home.showcase.analytics.factor4')}</span>
                      <div className="factor-bar-demo">
                        <div className="factor-fill-demo low" style={{ width: '34%' }}></div>
                      </div>
                      <span className="factor-score-demo">34</span>
                    </div>
                  </div>
                </div>

                {/* Sensitivity Donut */}
                <div className="sensitivity-widget-demo">
                  <div className="sensitivity-donut-demo">
                    <svg viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#dcfce7" strokeWidth="12"/>
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#22c55e" strokeWidth="12" strokeDasharray="188 64" strokeLinecap="round" transform="rotate(-90 50 50)"/>
                    </svg>
                    <div className="donut-center-demo">
                      <span className="donut-value-demo">75%</span>
                      <span className="donut-label-demo">{t('home.showcase.sensitivity.impact')}</span>
                    </div>
                  </div>
                  <span className="sensitivity-title-demo">{t('home.showcase.sensitivity.title')}</span>
                </div>

                {/* Key Insight */}
                <div className="insight-card-demo">
                  <div className="insight-icon-demo">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>
                  <div className="insight-content-demo">
                    <span className="insight-title-demo">{t('home.showcase.insight.title')}</span>
                    <span className="insight-text-demo">{t('home.showcase.insight.text')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== How Products Work Together ===== */}
      <section className="products-together">
        <div className="container">
          <div className="together-content">
            <h2>{t('engine.together.title')}</h2>
            <div className="products-table">
              <div className="table-row">
                <div className="table-product">
                  <span className="product-name blue">Synthetic Juror™</span>
                </div>
                <div className="table-focus">
                  {t('engine.together.syntheticJuror')}
                </div>
              </div>
              <div className="table-row">
                <div className="table-product">
                  <span className="product-name amber">Fact Finder™</span>
                </div>
                <div className="table-focus">
                  {t('engine.together.factFinder')}
                </div>
              </div>
              <div className="table-row">
                <div className="table-product">
                  <span className="product-name green">Trial Signal™</span>
                </div>
                <div className="table-focus">
                  {t('engine.together.trialSignal')}
                </div>
              </div>
            </div>
            <p className="together-tagline">
              {t('engine.together.tagline')}
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ sections={['platform', 'syntheticJuror', 'factFinder', 'speed']} />

      {/* CTA Section */}
      <section className="calibration section">
        <div className="container">
          <div className="calibration-content">
            <h2>{t('engine.calibration.title')}</h2>
            <p>{t('engine.calibration.description')}</p>
            <Link to="/contact?type=docs" className="btn btn-primary">{t('common.requestDocumentation')}</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnginePage;
